/**
 * Shared ornaments for the candy-crush style "story card" dialogue
 * chrome: pearl studs, corner sparks, and scattered sparkles. First
 * used by the first-launch tour (since removed); extracted here so other dialogues
 * (wheel-node preview, future onboarding moments) render the same
 * decorative language without copy-pasting SVGs.
 *
 * Each ornament accepts an optional {@code fill} colour so callers
 * can biome-tint the chrome — pass the biome's accent for the corner
 * sparks/sparkles to match the surrounding scene.
 */

const SPARK_PATH = 'M7 0 L8 6 L14 7 L8 8 L7 14 L6 8 L0 7 L6 6 Z';

const DEFAULT_PEARL =
  'radial-gradient(circle at 35% 30%, #FFE57F, #FFC40C 60%, #B07F00)';
const DEFAULT_CORNER_FILL = '#FFD66B';
const DEFAULT_SPARKLE_FILL = '#FFE57F';

/** Small gold pearl stud — sits inside a ribbon at left/right edges,
 * mimicking the nail-head studs on a candy-crush style banner. */
export function PearlStud({
  side,
  /** Override the default warm-gold radial gradient. CSS gradient
   * string — see {@link DEFAULT_PEARL} for shape. */
  background = DEFAULT_PEARL,
}: {
  side: 'left' | 'right';
  background?: string;
}) {
  return (
    <div
      aria-hidden
      className="absolute top-1/2 -translate-y-1/2"
      style={{
        [side === 'left' ? 'left' : 'right']: 6,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background,
        boxShadow:
          'inset 0 -1px 1px rgba(120,90,0,0.5), 0 1px 1px rgba(0,0,0,0.4)',
      }}
    />
  );
}

/** Small four-pointed gold spark stamped into a card corner. Faint
 * enough to feel like decoration, not iconography. */
export function CornerSpark({
  position,
  fill = DEFAULT_CORNER_FILL,
  size = 14,
  offset = 14,
  opacity = 0.6,
}: {
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  fill?: string;
  size?: number;
  offset?: number;
  opacity?: number;
}) {
  const isTop = position.startsWith('top');
  const isLeft = position.endsWith('left');
  return (
    <div
      aria-hidden
      className="absolute"
      style={{
        [isTop ? 'top' : 'bottom']: offset,
        [isLeft ? 'left' : 'right']: offset,
        width: size,
        height: size,
        opacity,
      }}
    >
      <svg viewBox="0 0 14 14" width={size} height={size}>
        <path d={SPARK_PATH} fill={fill} opacity={0.85} />
      </svg>
    </div>
  );
}

/** Tiny scattered sparkle — same four-pointed-star glyph as the
 * corner ornament but configurable in position, size, rotation. Used
 * to dust a card interior with subtle "fairy dust." */
export function Sparkle({
  top,
  left,
  size,
  rotation,
  fill = DEFAULT_SPARKLE_FILL,
  opacity = 0.45,
}: {
  top: string;
  left: string;
  size: number;
  rotation: number;
  fill?: string;
  opacity?: number;
}) {
  return (
    <div
      aria-hidden
      className="absolute"
      style={{
        top,
        left,
        width: size,
        height: size,
        opacity,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
      }}
    >
      <svg viewBox="0 0 14 14" width={size} height={size}>
        <path d={SPARK_PATH} fill={fill} />
      </svg>
    </div>
  );
}
